import * as Sentry from "@sentry/vue";
import { bigIntMax, bigIntMin } from "@umi/shared/bigint";
import { catchBlock } from "@umi/shared/catchBlock";
import { getChainManager, type Chain, type EvmChainTag } from "@umi/shared/chains";
import type { GasFeeEstimation } from "@umi/shared/managers/evm";
import type { Token } from "@umi/shared/types/token";
import type { HydratedWallet } from "@umi/shared/types/wallet";
import type { Address } from "viem";
import _ from "lodash";


export const loading = reactive({
  dispersing: false,
  token: false,
  nativeBalances: false,
  tokenBalances: false,
  approvalStatus: false,
  estimates: false,
  approving: false,
});


export const transferFormDisabled = computed(() => {
  return [
    loading.dispersing,
    loading.token,
    loading.nativeBalances,
    loading.tokenBalances
  ].reduce((acc, condition) => acc || condition, false);
});



export function* interleave(arrays: any[][]) {
  const iterators = arrays.map(arr => arr[Symbol.iterator]());
  let done = false;

  while (!done) {
    done = true;
    for (const iterator of iterators) {
      const next = iterator.next();
      if (!next.done) {
        yield next.value;
        done = false;
      }
    }
  }
}


type AssignMode = "FLAT" | "FILLING" | "SPLIT";
type ProcessResult<T extends boolean> = T extends true
  ? boolean
  : Record<string, { to: string, value: bigint | bigint[] }[]>;


/**
 * Unified function that can either check if assignment is possible or perform the assignment
 * @param mode - Assignment mode
 * @param senders - Sender wallets
 * @param recipients - Recipient wallets
 * @param amount - Amount to assign
 * @param feeEstimates - Fee mapping
 * @param nativeBalances - Native balance mapping
 * @param tokenBalances - Token balance mapping
 * @param token - Token information
 * @param dryRun - If true, only check feasibility; if false, return transfers
 * @returns Boolean (if dryRun) or transfers object
 */
function processAssignment<T extends boolean>(
  mode: AssignMode,
  senders: HydratedWallet[],
  recipients: HydratedWallet[],
  amount: bigint,
  feeEstimates: Map<number, { fee: bigint }>,
  nativeBalances: Record<string, bigint>,
  tokenBalances: Record<string, bigint | bigint[]>,
  token: Token,
  dryRun: T
): ProcessResult<T> {

  const clonedNativeBalances = _.cloneDeep(nativeBalances) as Record<string, bigint>;
  const recipientCountMap = _.zipObject(
    senders.map(({ address }) => address),
    senders.map(() => 1)
  ) as Record<string, number>;


  /**
   * Process coin transfers (native or ERC20).
   * @returns Transfers object if not dry run, boolean otherwise
   */
  const processCoins = (): Record<string, { to: string; value: bigint; }[]> | boolean => {

    const isErc20   = token.standard === "erc20";
    const transfers = dryRun ? {} : _.zipObject(
      senders.map(({ address }) => address),
      senders.map(() => [])
    ) as Record<string, { to: string, value: bigint }[]>;

    // Native: contribution and gas share the same pool (native balance)
    // ERC20: contribution from token balance, gas from native balance (separate pools)
    const clonedCoinBalances = _.cloneDeep(
      isErc20 ? tokenBalances : nativeBalances
    ) as Record<string, bigint>;


    // Compute how much of the sender's balance is available for transfer
    const getTransferableBalance = (senderAddr: string, recipientCount: number) => {
      if (recipientCount === 0) return 0n;
      const transferFee = feeEstimates.get(recipientCount)?.fee || 0n;
      if (!isErc20) return bigIntMax((clonedCoinBalances[senderAddr] || 0n) - transferFee, 0n);
      // For ERC20 we check native balance for gas payment and token balance for the transfer itself
      if ((clonedNativeBalances[senderAddr] || 0n) < transferFee) return 0n;
      return bigIntMax(clonedCoinBalances[senderAddr] || 0n, 0n);
    };

    if (mode === "FLAT") {
      for (const recipient of recipients) {
        let amountToPool = amount;

        for (const sender of senders) {
          if (amountToPool === 0n) break;

          const senderRecipientCount = recipientCountMap[sender.address]!;
          const senderBalance        = getTransferableBalance(sender.address, senderRecipientCount);
          const senderContribution   = bigIntMin(senderBalance, amountToPool);

          if (senderContribution === 0n) continue;

          recipientCountMap[sender.address]!++;
          clonedCoinBalances[sender.address]! -= senderContribution;
          amountToPool -= senderContribution;

          if (!dryRun) {
            transfers[sender.address]!.push({
              to: recipient.address,
              value: senderContribution
            });
          }
        }

        if (dryRun && amountToPool > 0n) return false;
      }
    }

    else if (mode === "FILLING") {
      let amountFilled = 0n; // Keep track of what we filled
      for (const recipient of recipients) {

        const recipientCurrentBalance = clonedCoinBalances[recipient.address] || 0n;
        let amountNeeded = bigIntMax(0n, amount - recipientCurrentBalance);

        for (const sender of senders) {
          if (amountNeeded === 0n) break;

          const senderRecipientCount = recipientCountMap[sender.address]!;
          const senderBalance        = getTransferableBalance(sender.address, senderRecipientCount);
          const senderContribution   = bigIntMin(senderBalance, amountNeeded);

          if (senderContribution === 0n) continue;

          recipientCountMap[sender.address]!++;
          clonedCoinBalances[sender.address]! -= senderContribution;
          if (dryRun) amountFilled += senderContribution;
          amountNeeded -= senderContribution;

          if (!dryRun) {
            transfers[sender.address]!.push({
              to: recipient.address,
              value: senderContribution
            });
          }
        }

        if (dryRun && amountNeeded > 0n) return false;
      }
      // All recipients are already at or above the target amount
      if (dryRun && amountFilled === 0n) return false;
    }

    else if (mode === "SPLIT") {
      const totalRecipients = BigInt(recipients.length);
      const perRecipient    = amount / totalRecipients;
      let remainder         = amount % totalRecipients;

      for (const recipient of recipients) {

        let amountForRecipient = perRecipient + (remainder > 0n ? 1n : 0n);
        if (remainder > 0n) remainder--;

        for (const sender of senders) {
          if (amountForRecipient === 0n) break;

          const senderRecipientCount = recipientCountMap[sender.address]!;
          const senderBalance        = getTransferableBalance(sender.address, senderRecipientCount);
          const senderContribution   = bigIntMin(senderBalance, amountForRecipient);

          if (senderContribution === 0n) continue;

          recipientCountMap[sender.address]!++;
          clonedCoinBalances[sender.address]! -= senderContribution;
          amountForRecipient -= senderContribution;

          if (!dryRun) {
            transfers[sender.address]!.push({
              to: recipient.address,
              value: senderContribution
            });
          }
        }

        if (dryRun && amountForRecipient > 0n) return false;
      }
    }

    return dryRun ? true : transfers;
  };

  const processNfts = () => {

    const clonedNftBalances = _.cloneDeep(tokenBalances) as Record<string, bigint[]>;
    const transfers = _.zipObject(
      senders.map(({ address }) => address),
      senders.map(() => [])
    ) as Record<string, { to: string, value: bigint[] }[]>;

    /**
     * Interleave NFT balances of all senders so that sending
     * X NFTs does not exhaust the balance of a single sender.
     */
    const nfts = interleave(senders.map(({ address: sender }) => {
      const balance = clonedNftBalances[sender] || [];
      return balance.map(tokenId => ({ sender, tokenId }));
    })).toArray() as { sender: string, tokenId: bigint }[];


    // Helper function to attribute an NFT to a recipient in the transfers object
    const attributeNft = (nft: typeof nfts[0], recipient: string) => {
      const existingTransfer = transfers[nft.sender]?.find(t => t.to === recipient);
      if (existingTransfer) existingTransfer.value.push(nft.tokenId);
      else {
        transfers[nft.sender]!.push({
          to: recipient,
          value: [nft.tokenId]
        });
      }
    };


    /**
     * @description
     * In 'flat' mode, we send the same indicated @see amount
     * to all recipients, until we run out of NFTs.
     * @note Here @see amount means the number of NFTs to send
     * to each recipient.
     *
     * In dry run we check if we have enough NFTs
     * to send the indicated @see amount to each recipient.
     */
    if (mode === "FLAT") {
      const nftsNeededPer = Number(amount);
      if (dryRun) return nfts.length >= nftsNeededPer * recipients.length;
      let nftIndex = 0;
      for (const recipient of recipients) {
        for (let j = 0; j < Number(amount) && nftIndex < nfts.length; j++) {
          attributeNft(nfts[nftIndex++]!, recipient.address);
        }
      }
    }


    /**
     * @description
     * In 'filling' mode, we send NFTs to recipients until
     * their balance reach the indicated @see amount. If
     * recipient balance is already equal or greater than
     * the indicated @see amount, we skip it.
     * @note Here @see amount means the targeted amount of
     * NFTs each recipient should have after the transfer.
     *
     * In dry run we check if we have enough NFTs
     * to fill all recipients to the indicated @see amount.
     */
    else if (mode === "FILLING") {
      const tmpb = _.cloneDeep(clonedNftBalances);

      if (dryRun) {
        let totalNftsNeeded = 0;
        for (const recipient of recipients) {
          const recipientBalance = clonedNftBalances[recipient.address] || [];
          const needed = Math.max(0, Number(amount) - recipientBalance.length);
          totalNftsNeeded += needed;
        }
        return nfts.length >= totalNftsNeeded;
      }

      let nftIndex = 0;
      let progress = true;
      while (progress) {
        progress = false;
        for (const recipient of recipients) {
          if (nftIndex >= nfts.length) break;
          if ((tmpb[recipient.address]?.length ?? 0) >= Number(amount)) continue;
          tmpb[recipient.address]!.push(nfts[nftIndex]!.tokenId);
          attributeNft(nfts[nftIndex++]!, recipient.address);
          progress = true;
        }
      }
    }


    /**
     * @description
     * In 'split' mode, we send the indicated @see amount divided
     * equally between all recipients. Remainders are handled by
     * round robin.
     * @note We use the same round robin algorithm we used in 'flat' mode
     * but here we omit to check the recipient balance.
     * @note Here @see amount means the number of NFTs to split between
     * all recipients.
     *
     * In dry run we check if we have enough NFTs
     * to split the indicated @see amount between all recipients.
     */
    else if (mode === "SPLIT") {
      if (dryRun) return nfts.length >= Number(amount);

      const nftsToSplit = Math.min(Number(amount), nfts.length);
      for (let i = 0; i < nftsToSplit; i++) {
        const nft = nfts[i];
        if (!nft) break;
        attributeNft(nft, recipients[i % recipients.length]!.address);
      }

    }

    return dryRun ? true : transfers;
  };

  if (token.type === "coin") return processCoins() as ProcessResult<T>;
  return processNfts() as ProcessResult<T>;
}

export function processTransfers<T extends boolean>(
  mode: AssignMode,
  senders: HydratedWallet[],
  recipients: HydratedWallet[],
  amount: bigint,
  feeEstimates: Map<number, { fee: bigint }>,
  nativeBalances: Record<string, bigint>,
  tokenBalances: Record<string, bigint | bigint[]>,
  token: Token,
  options: { dryRun: T } = { dryRun: false } as { dryRun: T }
): ProcessResult<T> {
  return processAssignment(mode, senders, recipients, amount, feeEstimates, nativeBalances, tokenBalances, token, options.dryRun);
}


const feeEstimateCache = new Map<
  string,
  { estimates: Map<number, GasFeeEstimation>; disperseMode: "contract" | "direct" }
>();

export const clearFeeEstimateCache = () => feeEstimateCache.clear();

// Bumped on every call so a run that got superseded stops writing to the
// shared estimate state and stops owning the loading flag.
let estimateRun = 0;

/**
 * Estimate transfer fees for all possible transfers
 * so we can compute how many we can perform. Works for
 * both native and token transfers.
 */
export const estimateTransferFees = async (
  chain: Chain,
  token: Token,
  senders: HydratedWallet[],
  recipients: HydratedWallet[],
  tokenBalances: Record<string, bigint | bigint[]>,
  transferEstimates: Ref<Map<number, GasFeeEstimation>>,
  disperseMode: Ref<"contract" | "direct">,
) => {

  if (senders.length === 0 || recipients.length === 0) return;

  const run     = ++estimateRun;
  const isStale = () => run !== estimateRun;

  loading.estimates = true;

  const recipientCount = recipients.length;
  const tokenKey       = token.address ?? "native";

  /**
   * NFT fees are modelled off the largest sender balance, so two runs only
   * share a result when that balance matches too. Coin fees only depend on
   * the recipient count.
   */
  const balanceKey = token.type === "nft"
    ? _.max(senders.map(({ address }) => (tokenBalances[address] as bigint[] | undefined)?.length ?? 0)) ?? 0
    : "";

  const cacheKey       = `${chain.tag}:${tokenKey}:${recipientCount}:${balanceKey}`;
  const cachedEstimate = feeEstimateCache.get(cacheKey);

  const publish = (estimates: Map<number, GasFeeEstimation>, mode?: "contract" | "direct") => {
    if (isStale()) return;
    transferEstimates.value = estimates;
    if (mode) disperseMode.value = mode;
    // The table is filled in place as estimates land, so assigning it is not
    // always a reference change.
    triggerRef(transferEstimates);
  };

  const [,err] = await catchBlock(async () => {

    if (cachedEstimate) {
      publish(cachedEstimate.estimates, cachedEstimate.disperseMode);
      return;
    }

    if (chain.arch === "EVM") {
      const evm = getChainManager(chain.tag as EvmChainTag);
      const data = await evm.estimateTransfers({
        token,
        senders: _.map(senders, "address") as Address[],
        recipients: _.map(recipients, "address") as Address[],
        tokenBalances,
        // Surface the cheapest transfer shape as soon as it lands so the form
        // can settle instead of waiting on the whole table.
        onPartial: estimates => { publish(estimates); },
      });
      const mode = data.disperseMode ?? "contract";
      feeEstimateCache.set(cacheKey, { estimates: data.estimates, disperseMode: mode });
      publish(data.estimates, mode);
    }
  });

  // A newer run took over, it now owns both the estimates and the spinner.
  if (isStale()) return;

  loading.estimates = false;

  if (err) {
    console.error(err);
    Sentry.captureException(err);
    showErrorNotification("Failed to estimate fees. Please try again.");
    return;
  }
};
